/**
 * Native Toolbox generation input v1. The old unversioned native wizard uses
 * topicName/talkBody/attendees with different web destinations; it must be
 * archived and reviewed, never spread into this payload.
 *
 * A library topic is supplied separately by the project-bound catalogue.
 * The eventual server generator must re-read that active slug for this tenant
 * before using the builder: a client-provided topic object is not authority.
 */
import { z } from "zod";
import { AuthoritativeProjectFactsV1Schema } from "./schema";
import { INT32_MAX } from "../common";

const nonBlank = z.string().trim().min(1);
// Library identities are exact stored values; validation must not rename them.
const exactNonBlank = z.string().refine((value) => value.trim().length > 0);
const answerPair = z.object({ question: nonBlank, answer: z.string() }).strict();
const legislationRef = z.object({
  name: exactNonBlank,
  section: z.string().optional(),
  summary: z.string(),
}).strict();
// Catalogue rows cross the mobile wire permissively (#2411): additive fields
// from a newer server must never break an installed build's parse. The draft
// schema below keeps the strict pair/ref; the strict boundary at the bottom
// re-applies strictness server-side.
const catalogueAnswerPair = z.object({ question: nonBlank, answer: z.string() });
const catalogueLegislationRef = z.object({
  name: exactNonBlank,
  section: z.string().optional(),
  summary: z.string(),
});

export const ToolboxTopicCatalogueItemV1Schema = z.object({
  slug: exactNonBlank,
  category: exactNonBlank,
  name: exactNonBlank,
  description: z.string(),
  keyPoints: z.array(z.string()),
  dos: z.array(z.string()),
  donts: z.array(z.string()),
  questions: z.array(catalogueAnswerPair),
  reason: z.string().nullable(),
  legislationRefs: z.array(catalogueLegislationRef),
  planningHierarchy: z.array(z.string()),
  workerDuties: z.array(z.string()),
  discussionPrompts: z.array(z.string()),
  weatherNotes: z.string().nullable(),
  inspectionReqs: z.string().nullable(),
  realWorldExample: z.string().nullable(),
  guidanceRefs: z.array(z.string()),
  durationMinutes: z.number().int().positive().max(INT32_MAX).nullable(),
});
export type ToolboxTopicCatalogueItemV1 = z.infer<typeof ToolboxTopicCatalogueItemV1Schema>;

export const ToolboxTopicCatalogueResponseV1Schema = z.object({
  catalogueVersion: z.literal(1),
  topics: z.array(ToolboxTopicCatalogueItemV1Schema),
});
export type ToolboxTopicCatalogueResponseV1 = z.infer<typeof ToolboxTopicCatalogueResponseV1Schema>;

/** Server-response boundary re-applying what the mobile wire base drops
 * (#2411 doctrine, ToolboxLogResponseStrict pattern): the catalogue route
 * serialises through this so a shaped row fails server-side while an
 * installed build stays parseable across additive drift. */
export const ToolboxTopicCatalogueItemV1Strict = ToolboxTopicCatalogueItemV1Schema.extend({
  questions: z.array(answerPair),
  legislationRefs: z.array(legislationRef),
}).strict();
export const ToolboxTopicCatalogueResponseV1Strict = z.object({
  catalogueVersion: z.literal(1),
  topics: z.array(ToolboxTopicCatalogueItemV1Strict),
}).strict();
export type ToolboxTopicCatalogueResponseV1Strict = z.infer<typeof ToolboxTopicCatalogueResponseV1Strict>;

const deliveryDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((value) => {
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}, "Enter a valid delivery date");

export const ToolboxGenerationDraftV1Schema = z.object({
  generationDraftVersion: z.literal(1),
  documentType: z.literal("TOOLBOX_TALK"),
  answers: z.object({
    topicSlug: exactNonBlank,
    keyPoints: z.array(nonBlank).min(1),
    dos: z.array(nonBlank).min(1),
    donts: z.array(nonBlank).min(1),
    questions: z.array(answerPair),
    additionalNotes: z.string().nullable(),
    deliveredBy: nonBlank,
    deliveryDate,
    duration: nonBlank,
  }).strict(),
}).strict();
export type ToolboxGenerationDraftV1 = z.infer<typeof ToolboxGenerationDraftV1Schema>;

const toolboxHtml = z.string();
export const ToolboxGenerationPayloadV1Schema = z.object({
  confirmedProjectData: AuthoritativeProjectFactsV1Schema,
  selectedHazardSlugs: z.array(z.string()).length(0),
  preparedBy: z.string(),
  approvedBy: z.null(),
  // Nullable pair (#2805): library topics send the slug with customName
  // null; user-authored topics send null slug with the authored name. The
  // core links exactly what arrives (topicId null + customTopicName).
  toolboxTopicSlug: z.string().nullable(),
  toolboxCustomTopicName: z.string().nullable(),
  toolboxTopicName: exactNonBlank,
  toolboxKeyPoints: toolboxHtml,
  toolboxDos: toolboxHtml,
  toolboxDonts: toolboxHtml,
  toolboxQuestions: toolboxHtml,
  toolboxAdditionalNotes: z.string().nullable(),
  toolboxDeliveredBy: nonBlank,
  toolboxDeliveryDate: deliveryDate,
  toolboxDuration: nonBlank,
  toolboxReason: z.string().nullable(),
  toolboxLegislationHtml: toolboxHtml.nullable(),
  toolboxPlanningHtml: toolboxHtml.nullable(),
  toolboxWorkerDutiesHtml: toolboxHtml.nullable(),
  toolboxDiscussionPromptsHtml: toolboxHtml.nullable(),
  toolboxWeatherNotes: z.string().nullable(),
  toolboxInspectionReqs: z.string().nullable(),
  toolboxRealWorldExample: z.string().nullable(),
  toolboxGuidanceRefsHtml: toolboxHtml.nullable(),
  toolboxAttendanceHtml: toolboxHtml,
}).strict();
export type ToolboxGenerationPayloadV1 = z.infer<typeof ToolboxGenerationPayloadV1Schema>;

const ESCAPE_MAP: Record<string, string> = {
  "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
};
function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => ESCAPE_MAP[char] ?? char);
}
function htmlList(items: string[], ordered: boolean): string {
  const tag = ordered ? "ol" : "ul";
  return `<${tag}>${items.map((item) => `<li>${escapeHtml(item)}</li>`).join("")}</${tag}>`;
}
/**
 * A rich-topic list section, or null when the topic has nothing to say. Blank
 * entries are dropped first: a list of only empty strings is no list, and must
 * not print its heading over empty bullets. (The web wizard builder,
 * lib/toolbox-wizard-utils.ts, applies the same rule.)
 */
function richList(items: string[], className: string, heading: string, ordered = false): string | null {
  const kept = items.filter((item) => item.trim() !== "");
  if (!kept.length) return null;
  return `<div class="${className}"><h3>${heading}</h3>${htmlList(kept, ordered)}</div>`;
}

/**
 * Matches the web template's Toolbox keys and markup, with all text escaped.
 * The exact trusted catalogue name/slug win over any saved client label.
 */
export function buildCanonicalToolboxGenerationPayloadV1(
  draftInput: unknown,
  authoritativeProjectInput: unknown,
  activeTopicInput: unknown,
): ToolboxGenerationPayloadV1 {
  const draft = ToolboxGenerationDraftV1Schema.parse(draftInput);
  const project = AuthoritativeProjectFactsV1Schema.parse(authoritativeProjectInput);
  const topic = ToolboxTopicCatalogueItemV1Schema.parse(activeTopicInput);
  if (draft.answers.topicSlug !== topic.slug) {
    throw new Error("The selected Toolbox topic is no longer current");
  }
  const answers = draft.answers;
  const questionsHtml = answers.questions.map((item, index) =>
    `<div class="question-item"><p class="question"><strong>Q${index + 1}:</strong> ${escapeHtml(item.question)}</p><p class="answer"><em>A: ${escapeHtml(item.answer)}</em></p></div>`
  ).join("");
  const legislationHtml = topic.legislationRefs.length
    ? '<div class="legislation-section"><h3>Legal Context</h3>' + topic.legislationRefs.map((ref) =>
      `<div class="legislation-item"><strong>${escapeHtml(ref.name)}</strong>${ref.section ? ` (${escapeHtml(ref.section)})` : ""}<br/>${escapeHtml(ref.summary)}</div>`
    ).join("") + "</div>"
    : null;
  const attendanceRows = Array.from({ length: 15 }, () =>
    '<tr><td></td><td class="signature-cell"></td><td></td></tr>').join("");
  const attendanceHtml = '<div class="attendance-section"><h3>Attendance Record</h3>'
    + '<table class="attendance-table"><thead><tr><th>Name</th><th class="signature-cell">Signature</th><th>Date</th></tr></thead><tbody>'
    + attendanceRows + '</tbody></table><div class="delivery-record">'
    + `<p><strong>Delivered By:</strong> ${escapeHtml(answers.deliveredBy)}</p>`
    + `<p><strong>Date:</strong> ${escapeHtml(answers.deliveryDate)}</p>`
    + '<p><strong>Time:</strong> ________</p></div></div>';
  return ToolboxGenerationPayloadV1Schema.parse({
    confirmedProjectData: project,
    selectedHazardSlugs: [],
    preparedBy: answers.deliveredBy,
    approvedBy: null,
    toolboxTopicSlug: topic.slug,
    toolboxCustomTopicName: null,
    toolboxTopicName: topic.name,
    toolboxKeyPoints: `<div class="key-points"><h3>Key Points</h3>${htmlList(answers.keyPoints, true)}</div>`,
    toolboxDos: htmlList(answers.dos, false),
    toolboxDonts: htmlList(answers.donts, false),
    toolboxQuestions: questionsHtml,
    toolboxAdditionalNotes: answers.additionalNotes?.trim() ? escapeHtml(answers.additionalNotes.trim()) : null,
    toolboxDeliveredBy: answers.deliveredBy,
    toolboxDeliveryDate: answers.deliveryDate,
    toolboxDuration: answers.duration,
    toolboxReason: topic.reason ? escapeHtml(topic.reason) : null,
    toolboxLegislationHtml: legislationHtml,
    toolboxPlanningHtml: richList(topic.planningHierarchy, "planning-section", "Planning &amp; Controls", true),
    toolboxWorkerDutiesHtml: richList(topic.workerDuties, "worker-duties-section", "Worker Duties"),
    toolboxDiscussionPromptsHtml: richList(topic.discussionPrompts, "discussion-prompts-section", "Discussion Prompts"),
    toolboxWeatherNotes: topic.weatherNotes ? escapeHtml(topic.weatherNotes) : null,
    toolboxInspectionReqs: topic.inspectionReqs ? escapeHtml(topic.inspectionReqs) : null,
    toolboxRealWorldExample: topic.realWorldExample ? escapeHtml(topic.realWorldExample) : null,
    toolboxGuidanceRefsHtml: richList(topic.guidanceRefs, "guidance-refs-section", "Guidance References"),
    toolboxAttendanceHtml: attendanceHtml,
  });
}

/**
 * One bound for an authored topic name (the same product cap as custom
 * hazard and trade-task names), shared by the editor input, the autosave
 * schema, the wizard-draft route's envelope gate and this generation wire, so
 * no layer accepts a name another refuses. A layer that did would turn the
 * editor's save-before-submit into a recovery loop (#2821 follow-up).
 */
export const TOOLBOX_CUSTOM_TOPIC_NAME_MAX = 200;

/**
 * User-authored topic (#2805): no catalogue row - the speaker writes the
 * topic name, key points, dos/donts and Q&A themselves. The payload mirrors
 * the library builder with every catalogue-derived field null and
 * topicSlug null + customTopicName set, which the core links verbatim
 * (generate-document documentToolboxTopic: topicId null + customTopicName).
 */
export const ToolboxCustomGenerationDraftV1Schema = z.object({
  generationDraftVersion: z.literal(1),
  documentType: z.literal("TOOLBOX_TALK"),
  customTopicName: z.string().trim().min(1).max(TOOLBOX_CUSTOM_TOPIC_NAME_MAX),
  answers: z.object({
    keyPoints: z.array(nonBlank).min(1),
    dos: z.array(nonBlank).min(1),
    donts: z.array(nonBlank).min(1),
    questions: z.array(answerPair),
    additionalNotes: z.string().nullable(),
    deliveredBy: nonBlank,
    deliveryDate,
    duration: nonBlank,
  }).strict(),
}).strict();
export type ToolboxCustomGenerationDraftV1 = z.infer<typeof ToolboxCustomGenerationDraftV1Schema>;

export function buildCanonicalCustomToolboxGenerationPayloadV1(
  draftInput: unknown,
  authoritativeProjectInput: unknown,
): ToolboxGenerationPayloadV1 {
  const draft = ToolboxCustomGenerationDraftV1Schema.parse(draftInput);
  const project = AuthoritativeProjectFactsV1Schema.parse(authoritativeProjectInput);
  const answers = draft.answers;
  const questionsHtml = answers.questions.map((item, index) =>
    `<div class="question-item"><p class="question"><strong>Q${index + 1}:</strong> ${escapeHtml(item.question)}</p><p class="answer"><em>A: ${escapeHtml(item.answer)}</em></p></div>`
  ).join("");
  const attendanceRows = Array.from({ length: 15 }, () =>
    '<tr><td></td><td class="signature-cell"></td><td></td></tr>').join("");
  const attendanceHtml = '<div class="attendance-section"><h3>Attendance Record</h3>'
    + '<table class="attendance-table"><thead><tr><th>Name</th><th class="signature-cell">Signature</th><th>Date</th></tr></thead><tbody>'
    + attendanceRows + '</tbody></table><div class="delivery-record">'
    + `<p><strong>Delivered By:</strong> ${escapeHtml(answers.deliveredBy)}</p>`
    + `<p><strong>Date:</strong> ${escapeHtml(answers.deliveryDate)}</p>`
    + '<p><strong>Time:</strong> ________</p></div></div>';
  return ToolboxGenerationPayloadV1Schema.parse({
    confirmedProjectData: project,
    selectedHazardSlugs: [],
    preparedBy: answers.deliveredBy,
    approvedBy: null,
    toolboxTopicSlug: null,
    toolboxCustomTopicName: draft.customTopicName,
    toolboxTopicName: draft.customTopicName,
    toolboxKeyPoints: `<div class="key-points"><h3>Key Points</h3>${htmlList(answers.keyPoints, true)}</div>`,
    toolboxDos: htmlList(answers.dos, false),
    toolboxDonts: htmlList(answers.donts, false),
    toolboxQuestions: questionsHtml,
    toolboxAdditionalNotes: answers.additionalNotes?.trim() ? escapeHtml(answers.additionalNotes.trim()) : null,
    toolboxDeliveredBy: answers.deliveredBy,
    toolboxDeliveryDate: answers.deliveryDate,
    toolboxDuration: answers.duration,
    // Catalogue-derived sections are absent for a user-authored topic - the
    // template renders the section only when the field is present.
    toolboxReason: null,
    toolboxLegislationHtml: null,
    toolboxPlanningHtml: null,
    toolboxWorkerDutiesHtml: null,
    toolboxDiscussionPromptsHtml: null,
    toolboxWeatherNotes: null,
    toolboxInspectionReqs: null,
    toolboxRealWorldExample: null,
    toolboxGuidanceRefsHtml: null,
    toolboxAttendanceHtml: attendanceHtml,
  });
}
